import { useEffect, useState, useCallback } from "react";
import { Card, Col, Row } from "react-bootstrap";
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import { SpeedDial, SpeedDialAction } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { mdiEye, mdiFileEditOutline, mdiTrashCanOutline } from "@mdi/js";
import { toast } from "react-toastify";
import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Fab,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";

import { endpointPrincipal } from "../../../api/endPoints/endpointPrincipal/endpointPrincipal";
import ContainerTitulo from "../../../components/Formularios/Container/ContainerTitulo";
import { Button } from "../../../components/Formularios/Buttons/Button";
import { showConfirmationDialog } from "../../../components/Formularios/Modal/ModalExcluir";
import PaginacaoPadrao from "../../../components/Formularios/Paginacao/PaginacaoPadrao";

// Atualize a interface TicketInterface para corresponder aos dados da API
interface TicketInterface {
  id_ticket: number;
  titulo: string;
  tipo: string;
  contato: string[];
  empresa: string;
  empresa_filial: string;
  status: string;
  prioridade: string;
  grupo: string;
  createdAt: string;
  updatedAt: string;
  usuario_id: number;
  agente_id: number;
  agente_nome: string;
}

interface TicketResponse {
  data: TicketInterface[];
  total: number;
  totalPages: number;
}

// Interface para os agentes
interface AgenteInterface {
  id_agente: number;
  nome: string;
}

// Interface para os grupos
interface GrupoInterface {
  codigo: string;
  descricao: string;
}

// Interface para status e prioridades (mesma estrutura que grupos)
interface StatusPrioridadeInterface {
  codigo: string;
  descricao: string;
}

interface FilialInterface {
  id_filial: number;
  fantasia: string;
}

// Datas no formato AAAA-MM-DD (vazio = sem filtro)
interface PeriodosFiltro {
  criadoDe: string;
  criadoAte: string;
  fechadoDe: string;
  fechadoAte: string;
  vencimentoDe: string;
  vencimentoAte: string;
}

// Parâmetro da API para cada data dos filtros de período
const PARAMETROS_PERIODO: Record<keyof PeriodosFiltro, string> = {
  criadoDe: "criado_de",
  criadoAte: "criado_ate",
  fechadoDe: "fechado_de",
  fechadoAte: "fechado_ate",
  vencimentoDe: "vencimento_de",
  vencimentoAte: "vencimento_ate",
};

interface FiltrosLista {
  numero: string;
  agentes: number[];
  grupos: string[];
  status: string[];
  prioridades: string[];
  clientes: number[];
  periodos: PeriodosFiltro;
}

const filtrosLimpos: FiltrosLista = {
  numero: "",
  agentes: [],
  grupos: [],
  status: [],
  prioridades: [],
  clientes: [],
  periodos: {
    criadoDe: "",
    criadoAte: "",
    fechadoDe: "",
    fechadoAte: "",
    vencimentoDe: "",
    vencimentoAte: "",
  },
};

export const Ticket: React.FC = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<TicketInterface[]>([]);
  const [isCarregando, setIsCarregando] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(6);
  const [totalPages, setTotalPages] = useState(0);

  // Filtros
  const [numeroTicket, setNumeroTicket] = useState("");
  const [agentesFiltro, setAgentesFiltro] = useState<number[]>([]);
  const [gruposFiltro, setGruposFiltro] = useState<string[]>([]);
  const [periodosFiltro, setPeriodosFiltro] = useState<PeriodosFiltro>(
    filtrosLimpos.periodos
  );
  const [statusFiltro, setStatusFiltro] = useState<string[]>([]);
  const [prioridadeFiltro, setPrioridadeFiltro] = useState<string[]>([]);
  const [clientesFiltro, setClientesFiltro] = useState<number[]>([]);

  // Estado para armazenar as opções de select
  const [agenteOptions, setAgenteOptions] = useState<AgenteInterface[]>([]);
  const [grupoOptions, setGrupoOptions] = useState<GrupoInterface[]>([]);
  const [statusOptions, setStatusOptions] = useState<
    StatusPrioridadeInterface[]
  >([]);
  const [prioridadeOptions, setPrioridadeOptions] = useState<
    StatusPrioridadeInterface[]
  >([]);
  const [filialOptions, setFilialOptions] = useState<FilialInterface[]>([]);

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const excluirTicket = async (ticket: TicketInterface) => {
    // Verificar o status do ticket antes de prosseguir com a exclusão
    const status = ticket.status.toLowerCase();
    if (status === "fechado" || status === "resolvido") {
      toast.info("Tickets com status Fechado ou Resolvido não podem ser excluídos.");
      return; // Impede a exclusão
    }

    await showConfirmationDialog(
      "Excluir Ticket",
      `Tem certeza que deseja excluir o ticket ${ticket.id_ticket}?`,
      "question",
      "Excluir",
      "Cancelar"
    )
      .then((result) => {
        if (result.confirmed) {
          excluirTicketPorId(ticket.id_ticket);
        }
      })
      .catch((erro) => {
        toast.error(
          `Erro ao excluir registro. Motivo: ${erro.response.data.message}`
        );
      });
  };

  async function excluirTicketPorId(id: number) {
    setIsCarregando(true);
    try {
      await endpointPrincipal.delete(`tickets/${id}`);
      buscarTodosTickets();
      toast.success("Ticket excluído com sucesso");
    } catch (error: any) {
      toast.error(`Erro ao excluir : ${error.response.data.message}`);
    } finally {
      setIsCarregando(false);
    }
  }

  async function buscarTodosTickets(
    pagina: number = page,
    filtros?: FiltrosLista
  ) {
    const numero = filtros ? filtros.numero : numeroTicket;
    const agentes = filtros ? filtros.agentes : agentesFiltro;
    const grupos = filtros ? filtros.grupos : gruposFiltro;
    const status = filtros ? filtros.status : statusFiltro;
    const prioridades = filtros ? filtros.prioridades : prioridadeFiltro;
    const clientes = filtros ? filtros.clientes : clientesFiltro;
    const periodos = filtros ? filtros.periodos : periodosFiltro;

    setIsCarregando(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.set("page", String(pagina));
      queryParams.set("limit", String(limit));

      if (numero) queryParams.set("numero", numero);
      if (agentes.length) queryParams.set("agentes", agentes.join(","));
      if (grupos.length) queryParams.set("grupos", grupos.join(","));
      if (status.length) queryParams.set("status_lista", status.join(","));
      if (prioridades.length)
        queryParams.set("prioridades", prioridades.join(","));
      if (clientes.length) queryParams.set("clientes", clientes.join(","));
      (Object.keys(PARAMETROS_PERIODO) as (keyof PeriodosFiltro)[]).forEach(
        (campo) => {
          if (periodos[campo]) {
            queryParams.set(PARAMETROS_PERIODO[campo], periodos[campo]);
          }
        }
      );

      const response = await endpointPrincipal.get<TicketResponse>(
        `/tickets?${queryParams.toString()}`
      );

      const ticketsFormatados = response.data.data.map((ticket) => {
        // Garantir que contato seja sempre um array
        const contatoArray = Array.isArray(ticket.contato)
          ? ticket.contato
          : [ticket.contato].filter(Boolean);

        return {
          ...ticket,
          contato: contatoArray, // mantém como array
          createdAt: new Date(ticket.createdAt).toLocaleString(),
          updatedAt: new Date(ticket.updatedAt).toLocaleString(),
        };
      });

      setTickets(ticketsFormatados);
      setTotalPages(response.data.totalPages);

      // Carregar as opções de select
      await carregarOpcoesSelect();
    } catch (error: any) {
      toast.error(
        `Erro ao buscar tickets: ${error.response?.data?.message || "Erro na requisição"
        }`
      );
    } finally {
      setIsCarregando(false);
    }
  }

  // Função para carregar todas as opções de select de uma vez
  const carregarOpcoesSelect = useCallback(async () => {
    try {
      const [
        agentesResponse,
        gruposResponse,
        statusResponse,
        prioridadesResponse,
        filiaisResponse,
      ] = await Promise.all([
        endpointPrincipal.get<AgenteInterface[]>("tickets/agentes"),
        endpointPrincipal.get<GrupoInterface[]>(
          "codigosistema/detalhes/GRUPOS"
        ),
        endpointPrincipal.get<StatusPrioridadeInterface[]>(
          "codigosistema/detalhes/STATUS"
        ),
        endpointPrincipal.get<StatusPrioridadeInterface[]>(
          "codigosistema/detalhes/PRIORIDADES"
        ),
        endpointPrincipal
          .get<FilialInterface[]>("empresas-filiais")
          .catch(() => ({ data: [] as FilialInterface[] })),
      ]);

      setAgenteOptions(agentesResponse.data);
      setGrupoOptions(gruposResponse.data);
      setStatusOptions(statusResponse.data);
      setPrioridadeOptions(prioridadesResponse.data);
      setFilialOptions(
        [...(filiaisResponse.data || [])].sort((a, b) =>
          (a.fantasia || "").localeCompare(b.fantasia || "", "pt-BR")
        )
      );
    } catch (error: any) {
      toast.error(
        `Erro ao carregar opções dos campos: ${error.response?.data?.message || "Erro na requisição"
        }`
      );
    }
  }, []);

  useEffect(() => {
    buscarTodosTickets();
  }, [page, limit]); // Recarrega quando a página ou limite mudar

  // Carregar opções de select quando o componente for montado
  useEffect(() => {
    carregarOpcoesSelect();
  }, [carregarOpcoesSelect]);

  //prioridades baixa, media, alta, urgente
  const getPrioridadeColor = (prioridade: string) => {
    switch (prioridade.toLowerCase()) {
      case "urgente":
        return "#dc3545";
      case "alta":
        return "#da7b84";
      case "média":
        return "#ffc107";
      case "baixa":
        return "#28a745";
      default:
        return "#6c757d";
    }
  };

  // Função para limpar os filtros
  const limparFiltros = () => {
    setNumeroTicket(filtrosLimpos.numero);
    setAgentesFiltro(filtrosLimpos.agentes);
    setGruposFiltro(filtrosLimpos.grupos);
    setPeriodosFiltro(filtrosLimpos.periodos);
    setStatusFiltro(filtrosLimpos.status);
    setPrioridadeFiltro(filtrosLimpos.prioridades);
    setClientesFiltro(filtrosLimpos.clientes);
    if (page !== 1) {
      setPage(1);
    } else {
      buscarTodosTickets(1, filtrosLimpos);
    }
  };

  const aplicarFiltros = () => {
    if (page !== 1) {
      setPage(1);
    } else {
      buscarTodosTickets(1);
    }
  };

  const actions = [
    {
      icon: <AddIcon onClick={() => navigate("novo")} />,
      name: "Incluir",
    },
  ];

  // Par de datas De/Até de um filtro de período (o "Até" inclui o dia inteiro)
  const renderPeriodo = (
    titulo: string,
    campoDe: keyof PeriodosFiltro,
    campoAte: keyof PeriodosFiltro
  ) => {
    const atualizar = (campo: keyof PeriodosFiltro, valor: string) =>
      setPeriodosFiltro((atual) => ({ ...atual, [campo]: valor }));

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {titulo}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="De"
            value={periodosFiltro[campoDe]}
            onChange={(e) => atualizar(campoDe, e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: periodosFiltro[campoAte] || undefined }}
          />
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Até"
            value={periodosFiltro[campoAte]}
            onChange={(e) => atualizar(campoAte, e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: periodosFiltro[campoDe] || undefined }}
          />
        </Box>
      </Box>
    );
  };

  const renderFilters = () => (
    <div className="filters-content">
      <h6 className="text-uppercase mb-4 fw-bold filters-title">Filtros</h6>

      <TextField
        fullWidth
        size="small"
        label="Número do ticket"
        value={numeroTicket}
        onChange={(e) => setNumeroTicket(e.target.value.replace(/\D/g, ""))}
        sx={{ mb: 2 }}
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Cliente</InputLabel>
        <Select
          multiple
          size="small"
          value={clientesFiltro}
          onChange={(e) => {
            const value = e.target.value;
            const lista = typeof value === "string" ? value.split(",") : value;
            setClientesFiltro(lista.map((item) => Number(item)));
          }}
          label="Cliente"
          renderValue={(selected) =>
            (selected as number[])
              .map(
                (id) =>
                  filialOptions.find((filial) => filial.id_filial === id)
                    ?.fantasia || String(id)
              )
              .join(", ")
          }
          MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
        >
          {filialOptions.map((filial) => (
            <MenuItem key={filial.id_filial} value={filial.id_filial}>
              <Checkbox checked={clientesFiltro.includes(filial.id_filial)} />
              <ListItemText primary={filial.fantasia} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Agentes</InputLabel>
        <Select
          multiple
          size="small"
          value={agentesFiltro}
          onChange={(e) => {
            const value = e.target.value;
            const lista = typeof value === "string" ? value.split(",") : value;
            setAgentesFiltro(
              lista
                .map((item) => Number(item))
                .filter((item) => !Number.isNaN(item) && item > 0)
            );
          }}
          label="Agentes"
          renderValue={(selected) =>
            (selected as number[])
              .map(
                (id) =>
                  agenteOptions.find((agente) => agente.id_agente === id)
                    ?.nome || String(id)
              )
              .join(", ")
          }
        >
          {agenteOptions.map((agente) => (
            <MenuItem key={agente.id_agente} value={agente.id_agente}>
              <Checkbox checked={agentesFiltro.includes(agente.id_agente)} />
              <ListItemText primary={agente.nome} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Grupos</InputLabel>
        <Select
          multiple
          size="small"
          value={gruposFiltro}
          onChange={(e) => {
            const value = e.target.value;
            setGruposFiltro(
              typeof value === "string" ? value.split(",") : value
            );
          }}
          label="Grupos"
          renderValue={(selected) =>
            (selected as string[])
              .map(
                (codigo) =>
                  grupoOptions.find((grupo) => grupo.codigo === codigo)
                    ?.descricao || codigo
              )
              .join(", ")
          }
        >
          {grupoOptions.map((grupo) => (
            <MenuItem key={grupo.codigo} value={grupo.codigo}>
              <Checkbox checked={gruposFiltro.includes(grupo.codigo)} />
              <ListItemText primary={grupo.descricao} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {renderPeriodo("Criado", "criadoDe", "criadoAte")}
      {renderPeriodo("Fechado / Resolvido", "fechadoDe", "fechadoAte")}
      {renderPeriodo(
        "Resolução vence (somente tickets abertos)",
        "vencimentoDe",
        "vencimentoAte"
      )}

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Status</InputLabel>
        <Select
          multiple
          size="small"
          value={statusFiltro}
          onChange={(e) => {
            const value = e.target.value;
            setStatusFiltro(
              typeof value === "string" ? value.split(",") : value
            );
          }}
          label="Status"
          renderValue={(selected) =>
            (selected as string[])
              .map(
                (codigo) =>
                  statusOptions.find((status) => status.codigo === codigo)
                    ?.descricao || codigo
              )
              .join(", ")
          }
        >
          {statusOptions.map((status) => (
            <MenuItem key={status.codigo} value={status.codigo}>
              <Checkbox checked={statusFiltro.includes(status.codigo)} />
              <ListItemText primary={status.descricao} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Prioridades</InputLabel>
        <Select
          multiple
          size="small"
          value={prioridadeFiltro}
          onChange={(e) => {
            const value = e.target.value;
            setPrioridadeFiltro(
              typeof value === "string" ? value.split(",") : value
            );
          }}
          label="Prioridades"
          renderValue={(selected) =>
            (selected as string[])
              .map(
                (codigo) =>
                  prioridadeOptions.find(
                    (prioridade) => prioridade.codigo === codigo
                  )?.descricao || codigo
              )
              .join(", ")
          }
        >
          {prioridadeOptions.map((prioridade) => (
            <MenuItem key={prioridade.codigo} value={prioridade.codigo}>
              <Checkbox
                checked={prioridadeFiltro.includes(prioridade.codigo)}
              />
              <ListItemText primary={prioridade.descricao} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <div className="d-flex gap-2 mt-3">
        <Button
          type="button"
          className="btn w-100"
          style={{
            backgroundColor: "#f4811f",
            color: "white",
            border: "none",
            padding: "8px 16px",
            borderRadius: "4px",
          }}
          descricaoBotao="Aplicar Filtros"
          onMouseDown={(event) => event.preventDefault()}
          onclick={() => {
            aplicarFiltros();
            setMobileFiltersOpen(false);
          }}
        />
        <Button
          type="button"
          className="btn btn-outline-secondary w-100"
          style={{
            padding: "8px 16px",
            borderRadius: "4px",
          }}
          descricaoBotao="Limpar Filtros"
          onMouseDown={(event) => event.preventDefault()}
          onclick={() => {
            limparFiltros();
            setMobileFiltersOpen(false);
          }}
        />
      </div>
    </div>
  );

  return (
    <>
      <div className="container-fluid mt-2 p-0">
        <Row className="g-0">
          <Col lg={9} md={12} className="pe-lg-3">
            <div className="title-container d-flex justify-content-between align-items-center">
              <ContainerTitulo titulo="Tickets" />
              <div className="d-flex gap-2">
                {/* Botão de filtro em telas pequenas */}
                <Fab
                  size="small"
                  className="d-lg-none"
                  onClick={() => setMobileFiltersOpen(true)}
                  sx={{
                    position: "relative",
                    width: "36px",
                    height: "36px",
                    minHeight: "unset",
                    color: "white",
                    backgroundColor: "rgb(244, 129, 31)",
                    "&:hover": {
                      backgroundColor: "rgb(244, 129, 31, 0.9)",
                    },
                  }}
                >
                  <FilterListIcon sx={{ fontSize: "20px" }} />
                </Fab>

                {/* Botão de adicionar */}
                <Fab
                  size="small"
                  aria-label="add"
                  onClick={() => navigate("novo")}
                  sx={{
                    position: "relative",
                    width: "36px",
                    height: "36px",
                    minHeight: "unset",
                    marginRight: "8px",
                    color: "white",
                    // Laranja padrão dos botões de ação (mesmo do XC WMS)
                    backgroundColor: "#ffa226",
                    "&:hover": {
                      backgroundColor: "#ff9800",
                    },
                  }}
                >
                  <AddIcon sx={{ fontSize: "20px" }} />
                </Fab>
              </div>
            </div>

            {/* Lista de Tickets */}
            <div className="tickets-list-container">
              <div className="position-relative">
                {/* Cards dos tickets */}
                <div className="tickets-wrapper">
                  {tickets?.map((ticket) => (
                    <div
                      key={ticket.id_ticket}
                      className="ticket-card bg-white position-relative"
                      style={{
                        border: "1px solid rgba(0,0,0,0.08)",
                        transition: "all 0.2s ease-in-out",
                        cursor: "pointer",
                        // altura do card de fundo sera definida de acordo com o tamanho da tela
                        height: "80%",
                        // width: "100%",
                        // maxWidth: "100%",

                        // height: "150px",
                        // marginLeft: "0px",
                        // marginTop: "1px",
                        paddingTop: "10px",
                      }}
                    >
                      <Row className="g-0">
                        {/* Indicador de Prioridade */}
                        <Col xs="auto">
                          <div
                            className="priority-indicator"
                            style={{
                              width: "5px",
                              height: "100%",
                              backgroundColor: getPrioridadeColor(
                                ticket.prioridade
                              ),
                              borderTopLeftRadius: "4px",
                              borderBottomLeftRadius: "4px",
                            }}
                          />
                        </Col>

                        {/* Conteúdo Principal */}
                        <Col className="p-0">
                          <div className="ticket-card" onClick={() => {
                            if (ticket.status.toLowerCase() === "fechado") {
                              toast.info("Ticket com status Fechado não pode ser editado, somente visualizado.");
                              navigate(`visu/${ticket.id_ticket}`);
                            } else {
                              navigate(`editar/${ticket.id_ticket}`);
                            }
                          }}>
                            <div className="ticket-card-header d-flex justify-content-between align-items-center mb-2">
                              <div className="d-flex align-items-center">
                                <div className="ticket-avatar me-3">
                                  <span className="avatar-circle">{ticket.agente_nome?.charAt(0)}</span>
                                </div>
                                <div>
                                  <strong className="ticket-title">#{ticket.id_ticket} {ticket.titulo}</strong>
                                  <div className="text-muted small">{ticket.empresa_filial || ticket.empresa}</div>
                                </div>
                              </div>
                              <div className="ticket-datetime text-muted small">{ticket.createdAt}</div>
                            </div>

                            <div className="ticket-card-body d-flex justify-content-between align-items-center">
                              <div className="d-flex flex-column">
                                <span className="badge rounded-pill text-bg-light text-dark border">
                                  {ticket.status}
                                </span>
                                <span className="badge rounded-pill mt-1"
                                  style={{
                                    backgroundColor: `${getPrioridadeColor(ticket.prioridade)}20`,
                                    color: getPrioridadeColor(ticket.prioridade)
                                  }}
                                >
                                  {ticket.prioridade}
                                </span>
                              </div>

                              <div className="text-end text-muted small">
                                <div>{ticket.agente_nome}</div>
                                <div>{ticket.grupo}</div>
                              </div>

                              <div className="ticket-card-actions ms-3">
                                <Button
                                  onclick={() => navigate(`visu/${ticket.id_ticket}`)}
                                  icone={mdiEye}
                                  type="button"
                                  className="action-button"
                                  title={"Visualizar"}
                                />
                                <Button
                                  onclick={() => excluirTicket(ticket)}
                                  icone={mdiTrashCanOutline}
                                  type="button"
                                  className="action-button"
                                  title={"Excluir"}
                                />
                              </div>
                            </div>
                          </div>
                        </Col>

                      </Row>
                    </div>
                  ))}
                </div>




              </div>
            </div>
            <div className="tickets-list-rodape">
              {/* Paginação */}
              {totalPages > 0 && (
                <PaginacaoPadrao
                  paginaAtual={page}
                  totalPaginas={totalPages}
                  setPaginaAtual={setPage}
                />
              )}
            </div>
          </Col>

          {/* Filtros em telas grandes */}
          <Col lg={3} className="filters-container px-0 d-none d-lg-block">
            <Card className="border-0 shadow-sm mx-0">
              <Card.Body>
                {renderFilters()}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Drawer para filtros em telas pequenas */}
      <Drawer
        anchor="right"
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "100%",
            maxWidth: "320px",
            padding: "16px",
          },
        }}
      >
        {renderFilters()}
      </Drawer>

      {/* Estilos CSS */}
      <style>
        {`
          .container-fluid {
            max-width: 100%;
            overflow-x: hidden;
          }

          .tickets-wrapper {
            padding-top: 0;
            position: relative;
          }

          .ticket-card {
            margin-bottom: 1px;
            border-radius: 0;
          }

          .ticket-content {
            padding: 10px 8px;
          }

          .ticket-header {
            margin-bottom: 8px;
          }

          .ticket-title {
            font-size: 0.95rem;
            line-height: 1.2;
            margin-right: 8px;
          }

          .ticket-number {
            font-weight: 600;
            color: #666;
          }

          .ticket-text {
            color: #2c3e50;
            font-weight: 500;
          }

          .ticket-details {
            font-size: 0.8rem;
            line-height: 1.3;
          }

          .ticket-metadata {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;
          }

          .priority-badge {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 0.8rem;
            font-weight: 500;
          }

          .status-badge {
            padding: 6px 12px;
            border-radius: 20px;
            font-size: 0.85rem;
            font-weight: 500;
            display: inline-block;
          }

          .assignment-info {
            display: flex;
            align-items: center;
            gap: 8px;
            color: #666;
            font-size: 0.85rem;
          }

          .separator {
            color: #ddd;
          }

          .action-button {
            width: 28px;
            height: 28px;
            padding: 4px;
            border-radius: 4px;
            background: transparent;
            border: none;
            color: #666;
            transition: all 0.2s ease;
          }

          .action-button:hover {
            background: rgba(0,0,0,0.05);
            color: #333;
          }

          .sticky-filters {
            position: sticky;
            top: 20px;
          }

          .filters-title {
            color: #2c3e50;
            font-size: 0.9rem;
            letter-spacing: 0.5px;
          }

          .status-container {
            min-width: 130px;
            text-align: right;
          }

          .status-badge,
          .priority-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 0.75rem;
            padding: 6px 12px;
            border-radius: 12px;
            white-space: nowrap;
            line-height: 1;
            min-height: 24px;
            box-sizing: border-box;
          }

          /* Ajustes específicos para Galaxy Note e dispositivos similares */
          @media screen and (max-width: 412px) {
            .container-fluid {
              padding: 0;
            }

            .tickets-wrapper {
              margin: 0;
            }

            .ticket-card {
              border-radius: 0;
              border-left: none;
              border-right: none;
              margin: 0;
              border-bottom: 1px solid rgba(0,0,0,0.08);
            }

            .ticket-card:first-child {
              border-top: 1px solid rgba(0,0,0,0.08);
            }

            .ticket-content {
              padding: 8px 6px;
            }

            .priority-indicator {
              width: 3px;
            }

            .ticket-title {
              font-size: 0.85rem;
              padding-right: 4px;
            }

            .status-badge,
            .priority-badge {
              font-size: 0.7rem;
              padding: 5px 10px;
              min-height: 22px;
              width: 90%;
            }

            .ticket-details {
              font-size: 0.7rem;
            }

            .action-button {
              width: 24px;
              height: 24px;
              padding: 3px;
            }

            .ticket-actions {
              gap: 2px;
            }

            .status-container {
              min-width: 140px;
            }

            .ticket-metadata-line {
              gap: 8px;
            }

            .ticket-metadata-line .d-flex {
              gap: 4px !important;
            }

            /* Ajustes para o botão em telas menores */
            .MuiFab-root {
              transform: scale(0.9);
            }
          }

          /* Ajustes para telas muito pequenas */
          @media screen and (max-width: 320px) {
            .ticket-content {
              padding: 6px 4px;
            }

            .action-button {
              width: 22px;
              height: 22px;
              padding: 2px;
            }

            .ticket-actions {
              gap: 1px;
            }

            .status-container {
              min-width: 110px;
            }

            .status-badge,
            .priority-badge {
              font-size: 0.65rem;
              padding: 4px 8px;
              min-height: 20px;
              width: 90%;
            }

            .ticket-metadata-line {
              gap: 6px;
            }

            .ticket-metadata-line .d-flex {
              gap: 3px !important;
            }
          }

          /* Estilo para telas maiores */
          @media screen and (min-width: 413px) {
            .ticket-card {
              margin: 4px 8px;
              border-radius: 8px;
            }

            .tickets-wrapper {
              padding: 0 8px;
            }

            .status-container {
              min-width: 140px;
            }

            .status-badge,
            .priority-badge {
              padding: 6px 14px;
              min-height: 26px;
              width: 90%;
            }

            .ticket-metadata-line {
              gap: 12px;
            }
          }

          /* Ajuste para o botão flutuante */
          .MuiFab-root {
            box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
          }

          .MuiFab-root:hover {
            box-shadow: 0 4px 12px rgba(0,0,0,0.2) !important;
          }

          .title-container {
            padding: 8px 16px;
            margin-bottom: 8px;
          }

          .filters-container {
            margin-top: 14px; /* Ajusta a posição dos filtros para alinhar com o título */
          }

          .filters-container .card {
            margin-top: 48px;
          }

          @media screen and (max-width: 991px) {
            .filters-container {
              margin-top: 0;
            }
            
            .filters-container .card {
              margin-top: 8px;
            }
          }

          @media screen and (max-width: 412px) {
            .title-container {
              padding: 8px;
            }
          }

          /* Estilos para o drawer de filtros */
          .filters-content {
            height: 100%;
            overflow-y: auto;
          }
        `}
      </style>
    </>
  );
};
